import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiFolder, FiGrid, FiList, FiArrowRight, FiSearch, FiFilter, FiX } from 'react-icons/fi';
import environment from '../../../config/environment';
import { CAUSE_CATEGORIES, CAUSE_STATUSES, NTEE_CODES, categories } from '../../../constants/causeConstants';
import './CausesList.css';
import { toast } from 'react-hot-toast';

const CausesList = () => {
  const [causes, setCauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [searchQuery, setSearchQuery] = useState('');
  const { apiUrl } = environment();
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [nteeFilter, setNteeFilter] = useState('all');
  const [selectedCause, setSelectedCause] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);
  const navigate = useNavigate();

  // Define fallback categories if the imported ones aren't available
  const defaultCategories = [
    'Education',
    'Health & Wellness',
    'Environment',
    'Arts & Culture',
    'Human Services',
    'Animal Welfare',
    'Community Development',
    'Civil Rights',
    'Research & Innovation'
  ];

  // Get categories array safely
  const getCategoriesArray = () => {
    // Check if CAUSE_CATEGORIES is an array
    if (Array.isArray(CAUSE_CATEGORIES)) {
      return CAUSE_CATEGORIES;
    }
    
    // Check if categories is an array
    if (Array.isArray(categories)) {
      return categories;
    }
    
    // Check if CAUSE_CATEGORIES is an object with values
    if (CAUSE_CATEGORIES && typeof CAUSE_CATEGORIES === 'object') {
      return Object.values(CAUSE_CATEGORIES);
    }
    
    // Return default categories as fallback
    return defaultCategories;
  };

  useEffect(() => {
    const fetchCauses = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        console.log('Fetching causes with token:', token ? 'Token exists' : 'No token');
        
        const response = await fetch('/api/causes', {
          headers: {
            'Authorization': `Bearer ${token || ''}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch causes: ${response.status} ${response.statusText}`);
        }

        const responseText = await response.text();
        console.log('Raw response text:', responseText);
        setRawResponse(responseText);
        
        let data;
        try {
          data = JSON.parse(responseText);
          console.log('Parsed data:', data);
        } catch (parseError) {
          console.error('Error parsing JSON:', parseError);
          throw new Error(`Failed to parse response: ${parseError.message}`);
        }
        
        // Extract causes from the specific response format
        let extractedCauses = [];
        
        if (data && data.status === 'success' && data.data && Array.isArray(data.data.causes)) {
          // Format: { status: 'success', data: { causes: [...] } }
          extractedCauses = data.data.causes;
          console.log('Setting causes from data.data.causes:', extractedCauses.length);
        } else if (data && Array.isArray(data) && data.length === 2 && data[0] === 'success' && data[1] && data[1].causes) {
          // Format: ['success', { causes: [...] }]
          extractedCauses = data[1].causes;
          console.log('Setting causes from array format:', extractedCauses.length);
        } else if (data && Array.isArray(data)) {
          // Format: direct array of causes
          extractedCauses = data;
          console.log('Setting causes from direct array:', extractedCauses.length);
        } else if (data && typeof data === 'object') {
          // Format: object with causes property or other properties
          if (data.causes && Array.isArray(data.causes)) {
            extractedCauses = data.causes;
            console.log('Setting causes from object.causes:', extractedCauses.length);
          } else {
            // Try to extract from object values
            const possibleCauses = Object.values(data);
            if (possibleCauses.length > 0 && Array.isArray(possibleCauses[0])) {
              extractedCauses = possibleCauses[0];
              console.log('Setting causes from object values:', extractedCauses.length);
            } else {
              extractedCauses = possibleCauses;
              console.log('Setting causes from object values:', extractedCauses.length);
            }
          }
        }
        
        console.log('Extracted causes:', extractedCauses);
        setCauses(extractedCauses);
        setError(null);
      } catch (err) {
        console.error('Error fetching causes:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCauses();
  }, [apiUrl]);

  // Handle view cause action
  const handleViewCause = (causeId) => {
    navigate(`/admin/causes/${causeId}`);
  };

  const renderFilters = () => {
    return (
      <div className="filters-dropdown">
        <div className="filter-group">
          <label>Status</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {Array.isArray(CAUSE_STATUSES) ? CAUSE_STATUSES.map((status, index) => (
              <option 
                key={index} 
                value={typeof status === 'string' ? status.toLowerCase() : status}
              >
                {typeof status === 'string' ? status : String(status)}
              </option>
            )) : (
              <option value="active">Active</option>
            )}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Category</label>
          <select 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {getCategoriesArray().map((category, index) => (
              <option 
                key={index} 
                value={typeof category === 'string' ? category.toLowerCase() : category}
              >
                {typeof category === 'string' ? category : String(category)}
              </option>
            ))}
          </select>
        </div>
        
        {NTEE_CODES && (
          <div className="filter-group">
            <label>NTEE Code</label>
            <select 
              value={nteeFilter} 
              onChange={(e) => setNteeFilter(e.target.value)}
            >
              <option value="all">All NTEE Codes</option>
              {Object.entries(NTEE_CODES).map(([code, description]) => (
                <option key={code} value={code}>
                  {code}: {description}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  const filteredCauses = causes.filter(cause => {
    console.log('Filtering cause:', cause?.name, cause?._id);
    
    // Search query filter
    const nameMatch = cause?.name ? cause.name.toLowerCase().includes(searchQuery.toLowerCase()) : false;
    const descriptionMatch = cause?.description ? cause.description.toLowerCase().includes(searchQuery.toLowerCase()) : false;
    const searchMatch = nameMatch || descriptionMatch || !searchQuery;
    
    // Status filter
    let statusMatch = true;
    if (statusFilter !== 'all') {
      const causeStatus = cause?.status ? cause.status.toLowerCase() : 'active';
      statusMatch = causeStatus === statusFilter;
    }
    
    // Category filter
    let categoryMatch = true;
    if (categoryFilter !== 'all') {
      const causeCategory = cause?.categories?.mainCategory ? 
        cause.categories.mainCategory.toLowerCase() : 
        (cause?.category ? cause.category.toLowerCase() : '');
      categoryMatch = causeCategory === categoryFilter;
    }
    
    console.log(`Cause ${cause?.name} matches: search=${searchMatch}, status=${statusMatch}, category=${categoryMatch}`);
    
    return searchMatch && statusMatch && categoryMatch;
  });

  const renderTableView = () => {
    return (
      <table className="causes-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Category</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredCauses.map((cause, index) => (
            <tr key={cause._id || index}>
              <td>{cause.name || 'Untitled Cause'}</td>
              <td>{cause.description || 'No description'}</td>
              <td>
                <span className={`status-badge ${(cause.status || 'active').toLowerCase()}`}>
                  {cause.status ? cause.status.charAt(0).toUpperCase() + cause.status.slice(1) : 'Active'}
                </span>
              </td>
              <td>
                {cause.categories?.mainCategory || 
                 (cause.categories && Array.isArray(cause.categories) && cause.categories.length > 0 
                  ? cause.categories[0] 
                  : 'Uncategorized')}
              </td>
              <td>
                <button 
                  className="view-button"
                  onClick={() => handleViewCause(cause._id)}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderGridView = () => {
    return (
      <div className="causes-grid">
        {filteredCauses.map((cause, index) => (
          <div key={cause._id || index} className="cause-card">
            <div className="cause-card-header">
              <h3>{cause.name || 'Untitled Cause'}</h3>
              <span className={`status-badge ${(cause.status || 'active').toLowerCase()}`}>
                {cause.status ? cause.status.charAt(0).toUpperCase() + cause.status.slice(1) : 'Active'}
              </span>
            </div>
            <p className="cause-description">{cause.description || 'No description'}</p>
            <div className="cause-category">
              <FiFolder />
              {cause.categories?.mainCategory || 
               (cause.categories && Array.isArray(cause.categories) && cause.categories.length > 0 
                ? cause.categories[0] 
                : 'Uncategorized')}
            </div>
            <button 
              className="view-button"
              onClick={() => handleViewCause(cause._id)}
            >
              View <FiArrowRight />
            </button>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading causes...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (!filteredCauses.length) {
    return (
      <div className="no-causes">
        <h2>Active Causes</h2>
        <p>No active causes found</p>
        <button onClick={() => {
          setSearchQuery('');
          setStatusFilter('all');
          setCategoryFilter('all');
        }} className="clear-filters-button">
          Clear Filters
        </button>
        {process.env.NODE_ENV === 'development' && (
          <div className="debug-info">
            <h4>Debug Information</h4>
            <p>Total causes: {causes.length}</p>
            <p>Filtered causes: {filteredCauses.length}</p>
            <p>Search query: "{searchQuery}"</p>
            <p>Status filter: "{statusFilter}"</p>
            <p>Category filter: "{categoryFilter}"</p>
            <details>
              <summary>Raw causes data ({Array.isArray(causes) ? causes.length : 0}):</summary>
              <pre>{JSON.stringify(causes, null, 2)}</pre>
            </details>
            <p>Raw response: {rawResponse}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="causes-list">
      <div className="causes-header">
        <h2>Internal Causes</h2>
        <div className="causes-controls">
          <div className="search-bar">
            <FiSearch />
            <input
              type="text"
              placeholder="Search causes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="filters-toggle">
            <FiFilter />
            {renderFilters()}
          </div>
          <div className="view-toggles">
            <button 
              className={`view-toggle ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid /> Grid
            </button>
            <button 
              className={`view-toggle ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <FiList /> Table
            </button>
          </div>
        </div>
      </div>
      {viewMode === 'table' ? renderTableView() : renderGridView()}
      
    
    </div>
  );
};

export default CausesList; 